import { ServerNetwork, ServerNetworkStatus } from '@/types/network.type';
import { DataWorker } from '@/workers/data.worker';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { isPlatform } from '@ionic/vue';
import { HubConnectionState } from '@microsoft/signalr';
import secureStorage from '@xpos/utils/src/secure.storage.utils';
import { Capacitor } from '@capacitor/core';




export const checkInCapacitor = () =>
  //@ts-ignore
  window['Capacitor'] && window['Capacitor'].getPlatform() !== 'web';

export const getDeviceId = async (): Promise<string> => {
  if (checkInCapacitor()) {
    const deviceInfo = await Device.getId();
    return deviceInfo.identifier;
  }
  // Backward compatible: use existing deviceId if stored
  const existingId = secureStorage.getItem('deviceId');
  if (existingId) return existingId;
  // New Electron device: use hardware fingerprint
  if (window.electron?.hardwareFingerprint) {
    try {
      const hwId = await window.electron.hardwareFingerprint();
      secureStorage.setItem('deviceId', hwId);
      return hwId;
    } catch {}
  }
  // Web fallback: generate UUID
  const uuid = generateUUID();
  secureStorage.setItem('deviceId', uuid);
  return uuid;
};

function generateUUID() {
  var d = new Date().getTime(); //Timestamp
  var d2 = (typeof performance !== 'undefined' && performance.now && performance.now() * 1000) || 0;
  return 'dev_xxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = Math.random() * 16; //random number between 0 and 16
    if (d > 0) {
      //Use timestamp until depleted
      r = (d + r) % 16 | 0;
      d = Math.floor(d / 16);
    } else {
      //Use microseconds since page-load if supported
      r = (d2 + r) % 16 | 0;
      d2 = Math.floor(d2 / 16);
    }
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// Get CDS hardware ID: use existing localStorage value if available, else POS hardware ID + "-cds"
export const getCDSDeviceId = async (): Promise<string> => {
  const stored = localStorage.getItem('cds_device_id');
  if (stored) return stored;
  const posId = await getDeviceId();
  return `${posId}-cds`;
}

let pending: Promise<Response> | null = null;
const timeoutSignal = (ms: number) => { try { return AbortSignal.timeout(ms) } catch { const c = new AbortController(); setTimeout(() => c.abort(), ms); return c.signal } };
export const hasInternet = (timeout = 5000) => pending ??= fetch('https://www.google.com/generate_204', {
  method: 'HEAD', mode: 'no-cors', cache: 'no-store', signal: timeoutSignal(timeout)
}).finally(() => pending = null);

export const getNetWorkStatus = async () => {
  if (Capacitor.isNativePlatform()) return await Network.getStatus();
  try { await hasInternet(); return { connected: true }; } catch { return { connected: false }; }
};

// Returns cleanup function. SignalR listeners registered once per connection (no unsubscribe API)
let signalrListenersRegistered = false;
export const upgradedNetworkListener = (callback: (status: ServerNetwork) => void): (() => void) => {
  const signalrConnection = DataWorker.getInstance().getConnection();
  const handleNetworkChange = (isOnline: boolean, status: ServerNetworkStatus) => callback({ isOnline, status });
  const getOfflineStatus = () => signalrConnection.state === HubConnectionState.Reconnecting ? ServerNetworkStatus.reconnecting : ServerNetworkStatus.closed;
  if (!signalrListenersRegistered) {
    signalrConnection.onclose(() => handleNetworkChange(false, ServerNetworkStatus.closed));
    signalrConnection.onreconnected(() => handleNetworkChange(true, ServerNetworkStatus.connected));
    signalrConnection.onreconnecting(() => handleNetworkChange(false, ServerNetworkStatus.reconnecting));
    signalrListenersRegistered = true;
  }
  // A freshly (re)built connection is already Connected here and never fires onreconnected - report it or the network-closed timeout from the old connection's teardown never gets cleared
  if (signalrConnection.state === HubConnectionState.Connected) handleNetworkChange(true, ServerNetworkStatus.connected);
  if (Capacitor.isNativePlatform()) {
    const handler = (status: { connected: boolean }) => {
      if (status.connected && signalrConnection.state === HubConnectionState.Connected) handleNetworkChange(true, ServerNetworkStatus.connected);
      else handleNetworkChange(false, getOfflineStatus());
    };
    const listener = Network.addListener('networkStatusChange', handler);
    return () => { listener.remove(); signalrListenersRegistered = false; };
  }
  const onOnline = () => { if (signalrConnection.state === HubConnectionState.Connected) handleNetworkChange(true, ServerNetworkStatus.connected); };
  const onOffline = () => handleNetworkChange(false, getOfflineStatus());
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); signalrListenersRegistered = false; };
};

// Basic network listener that only checks device connectivity
export const basicNetworkListener = (callback: (isOnline: boolean) => void) => {
  const checkInternet = async () => { try { await hasInternet(); callback(true); } catch { callback(false); } };
  checkInternet(); // Check initial state immediately
  if (Capacitor.isNativePlatform()) {
    const listener = Network.addListener('networkStatusChange', checkInternet);
    return () => listener.remove();
  } else {
    window.addEventListener('online', checkInternet);
    window.addEventListener('offline', checkInternet);
    return () => { window.removeEventListener('online', checkInternet); window.removeEventListener('offline', checkInternet); };
  }
};
